// ============================================================================
// CLEO'S SECRET — AuthVerify
// File: src/pages/AuthVerify.jsx
//
// Target of the magic-link click. Lives at /auth/verify?token=... (Vercel
// rewrite serves index.html at that path; App.jsx's getInitialPageFromUrl
// recognises the pathname and reads the token into routePayload before
// the URL_OVERRIDE_ACTIVE block strips the query string).
//
// SECURITY: DOES NOT auto-consume the token on mount. Email scanners (some
// corporate security gateways execute JavaScript) would pre-fetch the URL
// and consume the token before the user clicks. We render a brand-styled
// "Click to sign in" landing page; the verify POST fires only on user
// click. See docs/session-5-backlog entry on commit 6 carry-forward.
//
// Six-state machine driven by a single status string:
//   - idle          token present, awaiting user click
//   - verifying     POST in flight, button disabled
//   - success       brief "Welcome back" flash, auto-redirect to portal
//   - error-401     token invalid/expired/consumed (helper-specific message)
//   - error-other   network / 5xx / malformed; recoverable, retry button
//   - missing-token URL has no ?token=...; show same UX as 401 (no link)
// ============================================================================

import React, { useState } from 'react';
import { verifyMagicLinkToken } from '../lib/supabase-browser';
import { isValidReturnTo } from '../lib/valid-pages';
import {
  FF, P, T,
  BtnPrimary,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Page shell (shared by every state) ─────────
// Hoisted to module scope for the same reason MagicLinkLogin hoists its
// Shell — keeping the function reference stable across re-renders prevents
// React from treating it as a new component type and unmounting children.
function Shell(props) {
  return (
    <div style={{
      fontFamily: FF,
      background: P.SAND,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(40px, 6vw, 80px) clamp(20px, 4vw, 48px)',
    }}>
      <div style={{
        maxWidth: 600,
        width: '100%',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {props.children}
      </div>
    </div>
  );
}

// 1200ms is long enough for the user's eye to register the "Welcome back"
// flash before we route them. Easier to speed up later if testing shows
// sluggish than to slow down once users are accustomed to the rhythm.
var SUCCESS_REDIRECT_MS = 1200;

export default function AuthVerify(props) {
  var onNavigate = props.onNavigate;
  var token = props.token;
  // routePayload.returnTo is set when the user came in via a magic link
  // with &return=program (or similar) on the URL. Server already validated
  // against the allowlist before embedding; we double-validate client-side
  // before navigating to defend against URL tampering after-the-fact.
  var routePayload = props.routePayload || {};
  var rawReturnTo = routePayload.returnTo;
  var validatedReturnTo = isValidReturnTo(rawReturnTo) ? rawReturnTo : 'portal';

  // Initial status: idle if a token is present, missing-token otherwise.
  // The missing-token state renders the same UX as a 401 (no "try again"
  // since there's nothing to retry without a fresh email).
  var hasToken = token && typeof token === 'string' && token.length > 0;

  var statusState = useState(hasToken ? 'idle' : 'missing-token');
  var status = statusState[0];
  var setStatus = statusState[1];

  var firstNameState = useState(null);
  var firstName = firstNameState[0];
  var setFirstName = firstNameState[1];

  var errorState = useState('');
  var errorMessage = errorState[0];
  var setErrorMessage = errorState[1];

  function handleVerify() {
    setStatus('verifying');
    setErrorMessage('');
    verifyMagicLinkToken(token)
      .then(function(result) {
        setFirstName((result && result.first_name) || null);
        setStatus('success');
        // Brief flash so the user reads the welcome before navigation.
        // Destination is the validated returnTo (or 'portal' default).
        setTimeout(function() {
          if (onNavigate) onNavigate(validatedReturnTo);
        }, SUCCESS_REDIRECT_MS);
      })
      .catch(function(err) {
        var message = (err && err.message) || 'Sign-in failed. Please try again.';
        setErrorMessage(message);
        // STRING COUPLING: this comparison depends on the exact error message
        // thrown by verifyMagicLinkToken at the 401 branch. Future-proofing
        // backlog: refactor helper to return structured error codes
        // ({ status: 'expired' | 'network' | ... }) instead of string equality.
        // See docs/session-5-backlog.md entry on auth helper error codes.
        if (message === 'This link is invalid or has expired.') {
          setStatus('error-401');
        } else {
          setStatus('error-other');
        }
      });
  }

  function handleRequestNewLink() {
    if (onNavigate) onNavigate('login');
  }

  // ───────── Shared styles ─────────
  var titleStyle = {
    fontFamily: FF,
    fontSize: 'clamp(36px, 4.8vw, 60px)',
    fontWeight: 400,
    lineHeight: 1.08,
    letterSpacing: '-0.018em',
    color: P.INK,
    margin: '0 0 32px',
    maxWidth: 600,
  };
  var accentRule = {
    width: 'clamp(140px, 18vw, 220px)',
    height: 2,
    background: P.GO,
    opacity: 0.9,
    margin: '0 0 clamp(28px, 3vw, 40px)',
  };
  var ankhBlock = { marginBottom: 'clamp(24px, 2.5vw, 36px)' };
  var bodyCopy = {
    ...T.bodyLg,
    color: P.TX,
    maxWidth: 480,
    margin: '0 0 clamp(28px, 3vw, 40px)',
  };
  var errorCopy = {
    fontFamily: FF,
    fontSize: 'clamp(15px, 1.15vw, 17px)',
    fontStyle: 'italic',
    color: P.RED,
    maxWidth: 480,
    margin: '0 0 clamp(28px, 3vw, 40px)',
    lineHeight: 1.6,
  };
  var secondaryLink = {
    marginTop: 'clamp(28px, 3vw, 40px)',
    fontFamily: FF,
    fontSize: 'clamp(14px, 1.1vw, 16px)',
    color: P.TL,
    lineHeight: 1.7,
  };

  // ═════════════════════════════════════════════════════════════════════
  // SUCCESS STATE — brief welcome flash before auto-redirect
  // ═════════════════════════════════════════════════════════════════════
  if (status === 'success') {
    return (
      <Shell>
        <div style={ankhBlock}>
          <Ankh responsive="opener" color={P.GO} />
        </div>
        <h1 style={titleStyle}>
          <span style={{ display: 'block' }}>Welcome back{firstName ? ',' : '.'}</span>
          {firstName && (
            <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>
              {firstName}.
            </span>
          )}
        </h1>
        <div style={accentRule} />
        <p style={bodyCopy}>
          You&rsquo;re signed in. Taking you to your portal…
        </p>
      </Shell>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // ERROR-401 STATE — invalid / expired / already-consumed
  // ═════════════════════════════════════════════════════════════════════
  if (status === 'error-401' || status === 'missing-token') {
    return (
      <Shell>
        <div style={ankhBlock}>
          <Ankh responsive="opener" color={P.GO} />
        </div>
        <h1 style={titleStyle}>
          <span style={{ display: 'block' }}>Your link has expired</span>
          <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>or already been used.</span>
        </h1>
        <div style={accentRule} />
        <p style={bodyCopy}>
          Magic links can only be used once, and expire 30 minutes after they&rsquo;re sent. Request a fresh link to continue.
        </p>
        <BtnPrimary onClick={handleRequestNewLink} large>
          Request a new link
        </BtnPrimary>
      </Shell>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // ERROR-OTHER STATE — network / 5xx / malformed; recoverable
  // ═════════════════════════════════════════════════════════════════════
  if (status === 'error-other') {
    return (
      <Shell>
        <div style={ankhBlock}>
          <Ankh responsive="opener" color={P.GO} />
        </div>
        <h1 style={titleStyle}>
          <span style={{ display: 'block' }}>Something went wrong.</span>
          <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>Let&rsquo;s try again.</span>
        </h1>
        <div style={accentRule} />
        <p style={errorCopy}>{errorMessage}</p>
        <BtnPrimary onClick={handleVerify} large>
          Try again
        </BtnPrimary>
        <p style={secondaryLink}>
          Still not working?{' '}
          <span
            onClick={handleRequestNewLink}
            style={{
              fontStyle: 'italic',
              color: P.BR2,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: '0.18em',
              fontSize: '1.05em',
            }}>
            Request a new link.
          </span>
        </p>
      </Shell>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // IDLE / VERIFYING — landing page with click-to-sign-in button
  // ═════════════════════════════════════════════════════════════════════
  var verifying = status === 'verifying';
  return (
    <Shell>
      <div style={ankhBlock}>
        <Ankh responsive="opener" color={P.GO} />
      </div>
      <h1 style={titleStyle}>
        <span style={{ display: 'block' }}>Welcome back.</span>
        <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>Sign in to continue.</span>
      </h1>
      <div style={accentRule} />
      <p style={bodyCopy}>
        You&rsquo;re one click away. Press the button below to sign in securely to your portal.
      </p>
      <BtnPrimary onClick={handleVerify} large disabled={verifying}>
        {verifying ? 'Signing in…' : 'Sign in to your portal'}
      </BtnPrimary>
    </Shell>
  );
}
